import React, { useState, useEffect } from 'react';
import { api } from '../config/api';
import toast from 'react-hot-toast';

interface Subscription {
  id: number;
  site_name: string;
  monthly_price: number;
  billing_cycle: 'Monthly' | 'Quarterly' | 'Yearly';
  description: string;
  url: string;
  category: string;
  section: string;
  created_at: string;
  updated_at: string;
}

interface Summary {
  category: string;
  subscription_count: number;
  total_monthly_cost: number;
  total_yearly_cost: number;
}

const TECH_CATEGORIES = [
  'AI/LLM',
  'Development',
  'Data/Scraping',
  'Design',
  'Cloud Storage',
  'Video/Media',
  'Business Tools',
  'Hosting',
  'SaaS',
  'Other'
];

const categoryColors: Record<string, { bg: string; text: string; border: string }> = {
  'AI/LLM':         { bg: 'bg-purple-500/15', text: 'text-purple-400', border: 'border-purple-500/30' },
  'Development':    { bg: 'bg-cyan-500/15',   text: 'text-cyan-400',   border: 'border-cyan-500/30' },
  'Data/Scraping':  { bg: 'bg-amber-500/15',  text: 'text-amber-400',  border: 'border-amber-500/30' },
  'Design':         { bg: 'bg-pink-500/15',   text: 'text-pink-400',   border: 'border-pink-500/30' },
  'Cloud Storage':  { bg: 'bg-blue-500/15',   text: 'text-blue-400',   border: 'border-blue-500/30' },
  'Video/Media':    { bg: 'bg-red-500/15',    text: 'text-red-400',    border: 'border-red-500/30' },
  'Business Tools': { bg: 'bg-emerald-500/15', text: 'text-emerald-400', border: 'border-emerald-500/30' },
  'Hosting':        { bg: 'bg-orange-500/15', text: 'text-orange-400', border: 'border-orange-500/30' },
  'SaaS':           { bg: 'bg-indigo-500/15', text: 'text-indigo-400', border: 'border-indigo-500/30' },
  'Other':          { bg: 'bg-gray-500/15',   text: 'text-gray-400',   border: 'border-gray-500/30' },
};

const getCategoryStyle = (category: string) => {
  return categoryColors[category] || categoryColors['Other'];
};

export const TechStack: React.FC = () => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [summary, setSummary] = useState<Summary[]>([]);
  const [totalMonthly, setTotalMonthly] = useState(0);
  const [totalYearly, setTotalYearly] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    site_name: '',
    monthly_price: '',
    billing_cycle: 'Monthly' as 'Monthly' | 'Quarterly' | 'Yearly',
    description: '',
    url: '',
    category: 'AI/LLM'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [subsRes, summaryRes, totalsRes] = await Promise.all([
        api.get('/subscriptions/', { params: { section: 'tech_stack' } }),
        api.get('/subscriptions/summary', { params: { section: 'tech_stack' } }),
        api.get('/subscriptions/totals', { params: { section: 'tech_stack' } })
      ]);

      setSubscriptions(subsRes.data);
      setSummary(summaryRes.data);
      setTotalMonthly(totalsRes.data.total_monthly || 0);
      setTotalYearly(totalsRes.data.total_yearly || 0);
      setTotalCount(totalsRes.data.total_subscriptions || 0);
    } catch (error) {
      console.error('Error fetching tech stack:', error);
      toast.error('Failed to load tech stack');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        monthly_price: parseFloat(formData.monthly_price),
        section: 'tech_stack'
      };

      if (editingId) {
        await api.put(`/subscriptions/${editingId}`, data);
        toast.success('Service updated');
      } else {
        await api.post('/subscriptions/', data);
        toast.success('Service added to stack');
      }

      setShowForm(false);
      setEditingId(null);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save');
    }
  };

  const handleEdit = (sub: Subscription) => {
    setFormData({
      site_name: sub.site_name,
      monthly_price: sub.monthly_price.toString(),
      billing_cycle: sub.billing_cycle,
      description: sub.description || '',
      url: sub.url || '',
      category: sub.category
    });
    setEditingId(sub.id);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Remove this service from your tech stack?')) return;
    try {
      await api.delete(`/subscriptions/${id}`);
      toast.success('Service removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove');
    }
  };

  const resetForm = () => {
    setFormData({
      site_name: '',
      monthly_price: '',
      billing_cycle: 'Monthly',
      description: '',
      url: '',
      category: 'AI/LLM'
    });
  };

  const filteredSubs = activeCategory
    ? subscriptions.filter(s => s.category === activeCategory)
    : subscriptions;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-start mb-8">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-3xl font-bold text-white" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              <span className="text-cyan-400">~/</span>tech-stack
            </h1>
            <span className="text-gray-600 text-sm font-mono">v1.0</span>
          </div>
          <p className="text-gray-500 text-sm font-mono">
            <span className="text-green-500">$</span> tracking {totalCount} services
            <span className="text-gray-600 mx-2">|</span>
            burn rate: <span className="text-cyan-400">${totalMonthly.toFixed(2)}</span>/mo
          </p>
        </div>
        <button
          onClick={() => {
            setShowForm(!showForm);
            if (showForm) { setEditingId(null); resetForm(); }
          }}
          className="bg-cyan-600 hover:bg-cyan-700 text-white px-4 py-2 rounded-md font-mono text-sm transition-colors"
        >
          {showForm ? '[ cancel ]' : '+ add service'}
        </button>
      </div>

      {/* Burn Rate Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-gray-800/80 border border-gray-700/50 rounded-lg p-5">
          <div className="text-gray-500 text-xs font-mono uppercase tracking-wider mb-2">Monthly Burn</div>
          <div className="text-2xl font-bold text-cyan-400 font-mono">${totalMonthly.toFixed(2)}</div>
        </div>
        <div className="bg-gray-800/80 border border-gray-700/50 rounded-lg p-5">
          <div className="text-gray-500 text-xs font-mono uppercase tracking-wider mb-2">Annual Cost</div>
          <div className="text-2xl font-bold text-white font-mono">${totalYearly.toFixed(2)}</div>
        </div>
        <div className="bg-gray-800/80 border border-gray-700/50 rounded-lg p-5">
          <div className="text-gray-500 text-xs font-mono uppercase tracking-wider mb-2">Services</div>
          <div className="text-2xl font-bold text-white font-mono">{totalCount}</div>
        </div>
        <div className="bg-gray-800/80 border border-gray-700/50 rounded-lg p-5">
          <div className="text-gray-500 text-xs font-mono uppercase tracking-wider mb-2">Categories</div>
          <div className="text-2xl font-bold text-white font-mono">{summary.length}</div>
        </div>
      </div>

      {/* Category Filter Badges */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button
          onClick={() => setActiveCategory(null)}
          className={`px-3 py-1.5 rounded-md text-xs font-mono transition-all ${
            !activeCategory
              ? 'bg-cyan-600 text-white'
              : 'bg-gray-800 text-gray-400 hover:text-white border border-gray-700'
          }`}
        >
          all ({totalCount})
        </button>
        {summary.map(cat => {
          const style = getCategoryStyle(cat.category);
          const isActive = activeCategory === cat.category;
          return (
            <button
              key={cat.category}
              onClick={() => setActiveCategory(isActive ? null : cat.category)}
              className={`px-3 py-1.5 rounded-md text-xs font-mono transition-all border ${
                isActive
                  ? `${style.bg} ${style.text} ${style.border}`
                  : `bg-gray-800 text-gray-400 hover:text-white border-gray-700 hover:${style.border}`
              }`}
            >
              {cat.category.toLowerCase()} ({cat.subscription_count})
              <span className="ml-1.5 text-gray-500">${cat.total_monthly_cost.toFixed(0)}</span>
            </button>
          );
        })}
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-gray-800/80 border border-cyan-500/20 rounded-lg p-6 mb-8">
          <h2 className="text-lg font-mono text-cyan-400 mb-4">
            {editingId ? '> edit service' : '> add new service'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-gray-500 font-mono mb-1">service_name *</label>
                <input
                  type="text"
                  required
                  value={formData.site_name}
                  onChange={(e) => setFormData({ ...formData, site_name: e.target.value })}
                  placeholder="e.g., OpenAI"
                  className="w-full bg-gray-900 text-white rounded px-3 py-2 border border-gray-700 focus:border-cyan-500 focus:outline-none font-mono text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 font-mono mb-1">category *</label>
                <select
                  required
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full bg-gray-900 text-white rounded px-3 py-2 border border-gray-700 focus:border-cyan-500 focus:outline-none font-mono text-sm"
                >
                  {TECH_CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 font-mono mb-1">monthly_price *</label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500 font-mono text-sm">$</span>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={formData.monthly_price}
                    onChange={(e) => setFormData({ ...formData, monthly_price: e.target.value })}
                    className="w-full bg-gray-900 text-white rounded pl-7 pr-3 py-2 border border-gray-700 focus:border-cyan-500 focus:outline-none font-mono text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 font-mono mb-1">billing_cycle *</label>
                <select
                  required
                  value={formData.billing_cycle}
                  onChange={(e) => setFormData({ ...formData, billing_cycle: e.target.value as any })}
                  className="w-full bg-gray-900 text-white rounded px-3 py-2 border border-gray-700 focus:border-cyan-500 focus:outline-none font-mono text-sm"
                >
                  <option value="Monthly">Monthly</option>
                  <option value="Quarterly">Quarterly</option>
                  <option value="Yearly">Yearly</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 font-mono mb-1">url</label>
                <input
                  type="url"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  placeholder="https://..."
                  className="w-full bg-gray-900 text-white rounded px-3 py-2 border border-gray-700 focus:border-cyan-500 focus:outline-none font-mono text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 font-mono mb-1">description</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="What this service does"
                  className="w-full bg-gray-900 text-white rounded px-3 py-2 border border-gray-700 focus:border-cyan-500 focus:outline-none font-mono text-sm"
                />
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                className="bg-cyan-600 hover:bg-cyan-700 text-white px-6 py-2 rounded-md font-mono text-sm transition-colors"
              >
                {editingId ? 'update' : 'add'} service
              </button>
              <button
                type="button"
                onClick={() => { setShowForm(false); setEditingId(null); resetForm(); }}
                className="bg-gray-700 hover:bg-gray-600 text-gray-300 px-4 py-2 rounded-md font-mono text-sm transition-colors"
              >
                cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Category Breakdown */}
      {summary.length > 0 && !activeCategory && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-8">
          {summary.map(cat => {
            const style = getCategoryStyle(cat.category);
            const pct = totalMonthly > 0 ? (cat.total_monthly_cost / totalMonthly * 100) : 0;
            return (
              <button
                key={cat.category}
                onClick={() => setActiveCategory(cat.category)}
                className={`${style.bg} border ${style.border} rounded-lg p-4 text-left hover:brightness-110 transition-all`}
              >
                <div className={`text-xs font-mono ${style.text} mb-1`}>{cat.category}</div>
                <div className="text-white font-mono text-lg font-bold">${cat.total_monthly_cost.toFixed(0)}<span className="text-gray-500 text-xs">/mo</span></div>
                <div className="flex items-center gap-2 mt-2">
                  <div className="flex-1 h-1 bg-gray-700 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${style.text.replace('text-', 'bg-')}`} style={{ width: `${pct}%` }}></div>
                  </div>
                  <span className="text-gray-500 text-xs font-mono">{pct.toFixed(0)}%</span>
                </div>
                <div className="text-gray-500 text-xs font-mono mt-1">{cat.subscription_count} service{cat.subscription_count !== 1 ? 's' : ''}</div>
              </button>
            );
          })}
        </div>
      )}

      {/* Services Table */}
      <div className="bg-gray-800/80 border border-gray-700/50 rounded-lg overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-700/50 flex justify-between items-center">
          <span className="text-gray-400 text-sm font-mono">
            {activeCategory ? (
              <>
                <button onClick={() => setActiveCategory(null)} className="text-cyan-400 hover:text-cyan-300 mr-1">all</button>
                <span className="text-gray-600">/ </span>
                {activeCategory.toLowerCase()}
              </>
            ) : (
              'all services'
            )}
            <span className="text-gray-600 ml-2">({filteredSubs.length})</span>
          </span>
          {activeCategory && (
            <button
              onClick={() => setActiveCategory(null)}
              className="text-gray-500 hover:text-gray-300 text-xs font-mono"
            >
              clear filter
            </button>
          )}
        </div>
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-700/50">
              <th className="px-5 py-3 text-left text-xs font-mono text-gray-500 uppercase tracking-wider">Service</th>
              <th className="px-5 py-3 text-left text-xs font-mono text-gray-500 uppercase tracking-wider">Category</th>
              <th className="px-5 py-3 text-right text-xs font-mono text-gray-500 uppercase tracking-wider">Cost</th>
              <th className="px-5 py-3 text-left text-xs font-mono text-gray-500 uppercase tracking-wider">Cycle</th>
              <th className="px-5 py-3 text-left text-xs font-mono text-gray-500 uppercase tracking-wider hidden lg:table-cell">Description</th>
              <th className="px-5 py-3 text-right text-xs font-mono text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700/30">
            {filteredSubs.map((sub) => {
              const style = getCategoryStyle(sub.category);
              return (
                <tr key={sub.id} className="hover:bg-gray-700/20 transition-colors group">
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2">
                      {sub.url ? (
                        <a
                          href={sub.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-white hover:text-cyan-400 font-medium text-sm transition-colors"
                        >
                          {sub.site_name}
                          <span className="text-gray-600 ml-1 text-xs opacity-0 group-hover:opacity-100 transition-opacity">&nearr;</span>
                        </a>
                      ) : (
                        <span className="text-white font-medium text-sm">{sub.site_name}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-mono ${style.bg} ${style.text} border ${style.border}`}>
                      {sub.category}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    <span className="text-cyan-400 font-mono text-sm font-medium">${sub.monthly_price.toFixed(2)}</span>
                    <span className="text-gray-600 font-mono text-xs">/mo</span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="text-gray-400 text-xs font-mono">{sub.billing_cycle}</span>
                  </td>
                  <td className="px-5 py-3.5 hidden lg:table-cell">
                    <span className="text-gray-500 text-xs">{sub.description || ''}</span>
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    <div className="flex gap-3 justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => handleEdit(sub)}
                        className="text-gray-500 hover:text-cyan-400 text-xs font-mono transition-colors"
                      >
                        edit
                      </button>
                      <button
                        onClick={() => handleDelete(sub.id)}
                        className="text-gray-500 hover:text-red-400 text-xs font-mono transition-colors"
                      >
                        rm
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filteredSubs.length === 0 && (
          <div className="text-center py-12 text-gray-500 font-mono text-sm">
            {activeCategory
              ? `no services in ${activeCategory.toLowerCase()}`
              : 'no services in your tech stack yet'}
          </div>
        )}
        {/* Footer totals */}
        {filteredSubs.length > 0 && (
          <div className="border-t border-gray-700/50 px-5 py-3 flex justify-between items-center bg-gray-900/30">
            <span className="text-gray-500 text-xs font-mono">
              {filteredSubs.length} service{filteredSubs.length !== 1 ? 's' : ''}
            </span>
            <span className="font-mono text-sm">
              <span className="text-gray-500">total: </span>
              <span className="text-cyan-400 font-medium">
                ${filteredSubs.reduce((sum, s) => sum + s.monthly_price, 0).toFixed(2)}
              </span>
              <span className="text-gray-600">/mo</span>
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
